'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: 'Jak často se plán aktualizuje?',
      answer: 'Váš finanční plán aktualizujeme minimálně každých 6 měsíců při pravidelných schůzkách. Kromě toho můžete kdykoliv požádat o mimořádnou aktualizaci, pokud dojde k významné změně ve vašem životě – například změna zaměstnání, rodinná událost, nebo koupě nemovitosti.',
    },
    {
      question: 'Co když se moje situace výrazně změní?',
      answer: 'To je přesně důvod, proč používáme koncept živého dokumentu. Pokud dojde k významné změně, okamžitě se sejdeme (osobně nebo online) a upravíme váš plán podle nové situace. Nejste vázáni původní strategií – plán se přizpůsobuje vám, ne naopak.',
    },
    {
      question: 'Kolik stojí dlouhodobá spolupráce?',
      answer: 'Cena závisí na komplexnosti vaší situace a rozsahu služeb. První schůzka a diagnóza jsou zdarma. Po ní vám představíme konkrétní cenovou nabídku pro dlouhodobou spolupráci. Naše odměna je transparentní a vždy ji znáte předem.',
    },
    {
      question: 'Můžu kdykoliv ukončit spolupráci?',
      answer: 'Ano, spolupráci můžete ukončit kdykoliv bez sankcí. Věříme v dlouhodobé partnerství založené na důvěře a hodnotě, kterou přinášíme. Pokud nebudete spokojeni, není důvod pokračovat. Váš finanční plán zůstane váš a můžete s ním dále pracovat sami nebo s jiným poradcem.',
    },
    {
      question: 'Jak probíhají pravidelné schůzky?',
      answer: 'Pravidelné schůzky můžeme mít osobně v naší kanceláři v Praze-Chodově nebo online přes videohovor. Obvykle trvají 60-90 minut. Před schůzkou vám pošleme přehled aktuálního stavu a témat k probrání, abyste se mohli připravit.',
    },
    {
      question: 'Co když potřebuji poradit mezi schůzkami?',
      answer: 'Můžete nás kdykoliv kontaktovat emailem nebo telefonem. Na běžné dotazy odpovídáme do 24 hodin. Pokud jde o složitější situaci vyžadující delší konzultaci, domluvíme si mimořádnou schůzku.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-headline">
              Často kladené otázky
            </h2>
            <p className="text-lg text-text-secondary">
              Odpovědi na nejčastější dotazy o živém finančním plánu
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              
              return (
                <div
                  key={index}
                  className="bg-card rounded-xl shadow-card border border-border overflow-hidden transition-smooth hover:shadow-elevated"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left transition-smooth hover:bg-surface"
                  >
                    <span className="text-lg font-semibold text-text-primary pr-4">
                      {faq.question}
                    </span>
                    <Icon
                      name="ChevronDownIcon"
                      size={24}
                      className={`text-primary flex-shrink-0 transition-smooth ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden`}
                  >
                    <div className="px-6 pb-5 pt-2">
                      <p className="text-text-secondary leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-text-secondary mb-4">
              Máte další otázky? Rádi vám odpovíme.
            </p>
            <a
              href="/online-initial-diagnosis"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 hover:shadow-hover transition-smooth"
            >
              <span>Začít s diagnózou</span>
              <Icon name="ArrowRightIcon" size={20} className="text-current" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;